import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "pb_cookie_consent";

/**
 * AVG/GDPR-compliante cookie-banner.
 *
 * Toont pas cookies te laden voor "analytisch" tot de gebruiker expliciet
 * "Alles accepteren" klikt. "Alleen noodzakelijk" is ook expliciet. De keuze
 * wordt opgeslagen in localStorage zodat de banner niet elke pagina terugkomt.
 * Functionele cookies (login-sessie, taalvoorkeur) worden ALTIJD gezet — dat is
 * strikt noodzakelijk voor de dienst en vereist geen toestemming (art. 11.7a Tw).
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) setVisible(true);
    } catch (_) {
      // Private-mode Safari: banner tonen maar keuze niet persistent bewaren.
      setVisible(true);
    }
  }, []);

  const save = (choice) => {
    const payload = {
      choice, // 'all' | 'essential'
      at: new Date().toISOString(),
      analytics: choice === "all",
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (_) { /* Private mode — no-op */ }
    setVisible(false);
    // Dispatch custom event zodat analytics-scripts pas NA toestemming worden geladen.
    window.dispatchEvent(new CustomEvent("pb-cookie-consent", { detail: payload }));
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-labelledby="cookie-banner-title"
      className="fixed bottom-0 inset-x-0 z-[1000] p-3 md:p-4"
      data-testid="cookie-banner"
    >
      <div className="max-w-4xl mx-auto rounded-xl border-2 border-slate-900 bg-white shadow-[0_10px_30px_rgba(0,0,0,0.15)] p-4 md:p-5">
        <div className="flex flex-col md:flex-row md:items-start gap-4">
          <div className="hidden md:flex w-11 h-11 rounded-full bg-orange-100 items-center justify-center shrink-0">
            <Cookie size={22} className="text-orange-700"/>
          </div>
          <div className="flex-1 min-w-0">
            <div id="cookie-banner-title" className="pb-heading font-black text-lg text-slate-900">Cookies op Ploegbord</div>
            <p className="text-sm text-slate-700 font-medium mt-1 leading-relaxed">
              Wij gebruiken <strong>functionele cookies</strong> zodat je ingelogd kunt blijven en de app werkt.
              Deze zijn strikt noodzakelijk. Met jouw toestemming plaatsen wij ook <strong>analytische cookies</strong> om te
              begrijpen hoe de app gebruikt wordt. Geen tracking, geen advertenties, geen doorverkoop.
              {" "}
              Lees meer in onze <Link to="/privacy" className="text-orange-600 font-black underline hover:text-orange-800">Privacyverklaring</Link>.
            </p>
            {showDetails && (
              <div className="mt-3 space-y-2 text-xs text-slate-700 border-t border-slate-200 pt-3" data-testid="cookie-details">
                <div className="flex items-start gap-2">
                  <div className="w-24 shrink-0 font-black uppercase tracking-widest text-slate-500">Functioneel</div>
                  <div>Login-sessie (JWT-cookie), taalvoorkeur, thema. Bewaartijd: sessie of 30 dagen bij "onthou mij". <strong className="text-emerald-700">Altijd actief</strong> — geen toestemming vereist.</div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-24 shrink-0 font-black uppercase tracking-widest text-slate-500">Analytisch</div>
                  <div>Anonieme gebruiksstatistieken (pagina-bezoeken, gebruikte features). Bewaartijd: 12 maanden. <strong>Optioneel</strong> — alleen na expliciete toestemming.</div>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => setShowDetails((v) => !v)}
            className="hidden md:block absolute top-2 right-2 text-slate-400 hover:text-slate-700"
            aria-label={showDetails ? "Verberg details" : "Toon details"}
          >
            <X size={16}/>
          </button>
        </div>
        <div className="mt-4 flex flex-col-reverse md:flex-row gap-2 md:items-center md:justify-end">
          <button
            type="button"
            onClick={() => setShowDetails((v) => !v)}
            className="text-xs font-black uppercase tracking-widest text-slate-500 hover:text-slate-900 md:mr-auto"
            data-testid="cookie-details-toggle"
          >
            {showDetails ? "Verberg details" : "Details"}
          </button>
          <button
            type="button"
            onClick={() => save("essential")}
            className="pb-btn-outline !min-h-[44px] !px-4"
            data-testid="cookie-essential"
          >
            Alleen noodzakelijk
          </button>
          <button
            type="button"
            onClick={() => save("all")}
            className="pb-btn-primary !min-h-[44px] !px-6"
            data-testid="cookie-accept-all"
          >
            Alles accepteren
          </button>
        </div>
      </div>
    </div>
  );
}
